import { useEffect, useState, type FormEvent } from 'react';

import type { SysAdminOutputType } from '@shared/dto';

import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { PasswordInput } from '../../components/common/password-input';
import { ApiError, useApiLazyQuery, useApiMutation, useApiQuery } from '../../hooks/use-api';
import { sysAdminApi } from '../../api/sys-admin.api';

/** Modo à parte do app: sem `AuthProvider`/`AppShell`; uma rota (`/sys-admin`) cuida de login e do resto. */
export function SysAdminPage() {
  const [sysAdmin, setSysAdmin] = useState<SysAdminOutputType | null | undefined>(undefined);
  const { execute: verificarSessao } = useApiLazyQuery(sysAdminApi.me);

  useEffect(() => {
    verificarSessao()
      .then(setSysAdmin)
      .catch(() => setSysAdmin(null));
  }, []);

  if (sysAdmin === undefined) {
    return <Centralizado>Carregando...</Centralizado>;
  }

  if (!sysAdmin) {
    return <TelaLogin onEntrar={setSysAdmin} />;
  }

  return <Painel sysAdmin={sysAdmin} onSair={() => setSysAdmin(null)} />;
}

function Centralizado({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 text-sm text-muted-foreground">
      {children}
    </div>
  );
}

function TelaLogin({ onEntrar }: { onEntrar: (sysAdmin: SysAdminOutputType) => void }) {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const { mutate: entrar, loading } = useApiMutation(sysAdminApi.login, { silent: true });

  async function aoSubmeter(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    try {
      const { sysAdmin } = await entrar({ email, senha });
      onEntrar(sysAdmin);
    } catch (error) {
      setErro(error instanceof ApiError ? error.message : 'Nao foi possivel entrar.');
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Sys Admin</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={aoSubmeter} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="sysadmin-email">E-mail</Label>
              <Input
                id="sysadmin-email"
                type="email"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sysadmin-senha">Senha</Label>
              <PasswordInput
                id="sysadmin-senha"
                required
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
              />
            </div>
            {erro ? <p className="text-sm text-destructive">{erro}</p> : null}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Entrando...' : 'Entrar'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function Painel({ sysAdmin, onSair }: { sysAdmin: SysAdminOutputType; onSair: () => void }) {
  const { mutate: sair } = useApiMutation(sysAdminApi.logout, { silent: true });

  async function aoSair() {
    try {
      await sair();
    } finally {
      onSair();
    }
  }

  return (
    <div className="min-h-screen bg-muted/30 p-6">
      <div className="mx-auto max-w-3xl space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold">Sys Admin</h1>
            <p className="text-sm text-muted-foreground">{sysAdmin.email}</p>
          </div>
          <Button variant="outline" onClick={aoSair}>
            Sair
          </Button>
        </div>

        <Tabs defaultValue="usuarios">
          <TabsList>
            <TabsTrigger value="usuarios">Sys Admins</TabsTrigger>
            <TabsTrigger value="tenants">Tenants</TabsTrigger>
            <TabsTrigger value="features">Features</TabsTrigger>
          </TabsList>
          <TabsContent value="usuarios">
            <AbaUsuarios />
          </TabsContent>
          <TabsContent value="tenants">
            <AbaTenants />
          </TabsContent>
          <TabsContent value="features">
            <AbaFeatures />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function AbaUsuarios() {
  const { data: sysAdmins, loading, refetch } = useApiQuery(sysAdminApi.listarUsuarios, undefined);
  const { mutate: criar, loading: criando } = useApiMutation(sysAdminApi.criarUsuario);
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');

  async function aoCriar(e: FormEvent) {
    e.preventDefault();
    try {
      await criar({ email, senha });
      setEmail('');
      setSenha('');
      refetch();
    } catch {
      // useApiMutation ja mostra o toast de erro
    }
  }

  return (
    <div className="mt-4 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Novo sys admin</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={aoCriar} className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="novo-sysadmin-email">E-mail</Label>
              <Input
                id="novo-sysadmin-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="novo-sysadmin-senha">Senha</Label>
              <Input
                id="novo-sysadmin-senha"
                type="password"
                required
                minLength={8}
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={criando}>
              {criando ? 'Criando...' : 'Criar'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Sys admins</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">Carregando...</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>E-mail</TableHead>
                  <TableHead>Criado em</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(sysAdmins ?? []).map((sa) => (
                  <TableRow key={sa.id}>
                    <TableCell>{sa.email}</TableCell>
                    <TableCell>{new Date(sa.criadoEm).toLocaleString('pt-BR')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function AbaTenants() {
  const {
    data: tenants,
    loading: carregando,
    refetch,
  } = useApiQuery(sysAdminApi.listarTenants, undefined);
  const { mutate: criar, loading: criando } = useApiMutation(sysAdminApi.criarTenant);
  const { mutate: atualizar, loading: atualizando } = useApiMutation(sysAdminApi.atualizarTenant);
  const [nome, setNome] = useState('');
  const [slug, setSlug] = useState('');
  const [email, setEmail] = useState('');

  async function aoCriar(e: FormEvent) {
    e.preventDefault();
    try {
      await criar({
        nome,
        slug,
        administrador: { nome: 'Administrador inicial', email },
      });
      setNome('');
      setSlug('');
      setEmail('');
      await refetch();
    } catch {
      /* o hook exibe o erro */
    }
  }

  async function alternarStatus(id: string, status: 'ATIVO' | 'SUSPENSO') {
    try {
      await atualizar({ id, dados: { status } });
      await refetch();
    } catch {
      /* o hook exibe o erro */
    }
  }

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-base">Tenants</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={aoCriar} className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="tenant-nome">Unidade</Label>
            <Input
              id="tenant-nome"
              required
              value={nome}
              onChange={(e) => setNome(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tenant-slug">Slug</Label>
            <Input
              id="tenant-slug"
              required
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tenant-email">E-mail do admin</Label>
            <Input
              id="tenant-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <Button type="submit" disabled={criando}>
            {criando ? 'Criando...' : 'Criar tenant'}
          </Button>
        </form>
        {carregando ? (
          <p className="mt-3 text-sm text-muted-foreground">Carregando...</p>
        ) : (
          <Table className="mt-4">
            <TableHeader>
              <TableRow>
                <TableHead>Unidade</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(tenants ?? []).map((tenant) => (
                <TableRow key={tenant.id}>
                  <TableCell>{tenant.nome}</TableCell>
                  <TableCell>
                    <code>{tenant.slug}</code>
                  </TableCell>
                  <TableCell>{tenant.status}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={atualizando}
                      onClick={() =>
                        void alternarStatus(
                          tenant.id,
                          tenant.status === 'ATIVO' ? 'SUSPENSO' : 'ATIVO',
                        )
                      }
                    >
                      {tenant.status === 'ATIVO' ? 'Suspender' : 'Reativar'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

/** `listarTenants` já devolve `features`, então alternar uma célula só refaz essa busca. */
function AbaFeatures() {
  const {
    data: tenants,
    loading: carregandoTenants,
    refetch: refetchTenants,
  } = useApiQuery(sysAdminApi.listarTenants, undefined);
  const { data: catalogo, loading: carregandoCatalogo } = useApiQuery(
    sysAdminApi.listarFeatures,
    undefined,
  );
  const { mutate: alternar, loading: alternando } = useApiMutation(sysAdminApi.alternarFeature, {
    silent: true,
  });
  const [erro, setErro] = useState<string | null>(null);

  async function aoAlternar(tenantId: string, key: string, habilitada: boolean) {
    setErro(null);
    try {
      await alternar({ tenantId, key, dados: { habilitada } });
      await refetchTenants();
    } catch (error) {
      setErro(error instanceof ApiError ? error.message : 'Não foi possível atualizar a feature.');
    }
  }

  const carregando = carregandoTenants || carregandoCatalogo;

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-base">Features por tenant</CardTitle>
      </CardHeader>
      <CardContent>
        {erro ? <p className="mb-3 text-sm text-destructive">{erro}</p> : null}
        {carregando ? (
          <p className="text-sm text-muted-foreground">Carregando...</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Unidade</TableHead>
                {(catalogo ?? []).map((feature) => (
                  <TableHead key={feature.key} title={feature.descricao}>
                    {feature.nome}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {(tenants ?? []).map((tenant) => (
                <TableRow key={tenant.id}>
                  <TableCell>{tenant.nome}</TableCell>
                  {(catalogo ?? []).map((feature) => {
                    const habilitada = tenant.features.includes(feature.key);
                    return (
                      <TableCell key={feature.key}>
                        <Button
                          variant={habilitada ? 'default' : 'outline'}
                          size="sm"
                          disabled={alternando}
                          onClick={() => void aoAlternar(tenant.id, feature.key, !habilitada)}
                        >
                          {habilitada ? 'Habilitada' : 'Desabilitada'}
                        </Button>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
